import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import type { Task, TaskColumn, TaskTeam, CreateTaskPayload } from "@/features/tasks/types/task";
import { createTask, updateTask } from "@/features/tasks/services/taskService";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { Modal } from "@/shared/components/ui/Modal";
import { Button } from "@/shared/components/ui/Button";
import { Input } from "@/shared/components/ui/Input";

interface TaskFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: (task?: Task) => void;
  onError?: (err: any) => void;
  task?: Task | null;
  defaultColumnId?: string;
  columns: TaskColumn[];
  teams: TaskTeam[];
  /** When set, the task is forced to this team (team board context). */
  lockedTeamId?: string;
  /**
   * When set, the form is pre-filled from a chat message.
   * The source references will be stored on the created task.
   */
  sourceMessage?: {
    messageId: string;
    channelId: string;
    text: string;
    /** Channel members to pre-suggest as assignees (excluding self). */
    channelMembers?: Array<{
      user_id: string;
      first_name: string;
      middle_name?: string;
      last_name: string;
      profile_pic_id?: string;
    }>;
  };
}

export const TaskFormModal: React.FC<TaskFormModalProps> = ({
  isOpen,
  onClose,
  onSaved,
  onError,
  task,
  defaultColumnId,
  columns,
  lockedTeamId,
  sourceMessage,
}) => {
  const { t } = useTranslation();
  const { userData } = useAuth();
  const isEdit = !!task;

  const [title, setTitle]           = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory]     = useState("");
  const [columnId, setColumnId]     = useState("");
  const [deadline, setDeadline]     = useState("");
  const [remindAt, setRemindAt]     = useState("");
  const [titleError, setTitleError] = useState("");
  const [saving, setSaving]         = useState(false);

  // Reset / populate form when modal opens
  useEffect(() => {
    if (!isOpen) return;
    if (task) {
      setTitle(task.title);
      setDescription(task.description ?? "");
      setCategory(task.category ?? "");
      setColumnId(task.column_id ?? "");
      setDeadline(task.deadline ? task.deadline.split("T")[0] : "");
      setRemindAt(task.remind_at ? task.remind_at.slice(0, 16) : "");
    } else if (sourceMessage) {
      // Pre-fill from chat message
      const trimmedText = sourceMessage.text.slice(0, 200);
      setTitle(trimmedText);
      setDescription(sourceMessage.text);
      setCategory("");
      setColumnId(defaultColumnId ?? columns[0]?.id ?? "");
      setDeadline("");
      setRemindAt("");
    } else {
      setTitle("");
      setDescription("");
      setCategory("");
      setColumnId(defaultColumnId ?? columns[0]?.id ?? "");
      setDeadline("");
      setRemindAt("");
    }
    setTitleError("");
  }, [isOpen, task, defaultColumnId, columns, sourceMessage]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setTitleError(t("tasks.form.field_title_required"));
      return;
    }
    setTitleError("");
    setSaving(true);
    try {
      const payload: CreateTaskPayload = {
        title:             title.trim(),
        description:       description.trim() || undefined,
        category:          category.trim() || undefined,
        column_id:         columnId || undefined,
        deadline:          deadline || undefined,
        remind_at:         remindAt || undefined,
        // Personal board: auto-assign creator. Team board: no individual assignee.
        assignee_ids:      lockedTeamId ? [] : [String(userData?.id ?? "")].filter(Boolean),
        // Preserve the task's existing team_id when editing without an explicit locked context
        team_id:           lockedTeamId !== undefined ? (lockedTeamId || undefined) : (isEdit ? (task?.team_id ?? undefined) : undefined),
        source_message_id: sourceMessage?.messageId,
        source_channel_id: sourceMessage?.channelId,
      };
      if (isEdit && task) {
        await updateTask(task.id, payload);
        onSaved();
      } else {
        const created = await createTask(payload);
        onSaved(created);
      }
      onClose();
    } catch (err: any) {
      console.error("Failed to save task", err);
      onError?.(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        sourceMessage && !isEdit
          ? t("tasks.form.from_message_title", { defaultValue: "Create Task from Message" })
          : isEdit
          ? t("tasks.form.edit_title")
          : t("tasks.form.new_title")
      }
      size="xl"
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">

        {/* Source message hint */}
        {sourceMessage && !isEdit && (
          <div className="flex items-start gap-2 px-3 py-2 bg-blue-50 border border-blue-100 rounded-lg text-xs text-blue-700">
            <span className="font-medium shrink-0">{t("tasks.form.from_message_label", { defaultValue: "From chat:" })}</span>
            <span className="line-clamp-2 opacity-80">{sourceMessage.text}</span>
          </div>
        )}

        {/* Title */}
        <Input
          label={t("tasks.form.field_title")}
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            if (e.target.value.trim()) setTitleError("");
          }}
          placeholder={t("tasks.form.field_title_placeholder")}
          error={titleError}
          required
        />

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("tasks.form.field_description")}
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t("tasks.form.field_description_placeholder")}
            rows={3}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg resize-y focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>

        {/* Category */}
        <Input
          label={t("tasks.form.field_category")}
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          placeholder={t("tasks.form.field_category_placeholder")}
        />

        {/* Column */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("tasks.form.field_column")}
          </label>
          <select
            value={columnId}
            onChange={(e) => setColumnId(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="">—</option>
            {columns.map((c) => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </select>
        </div>

        {/* Deadline + Reminder */}
        <div className="grid grid-cols-2 gap-3">
          <Input
            label={t("tasks.form.field_deadline")}
            type="date"
            value={deadline}
            onChange={(e) => setDeadline(e.target.value)}
          />
          <Input
            label={t("tasks.form.field_remind_at")}
            type="datetime-local"
            value={remindAt}
            onChange={(e) => setRemindAt(e.target.value)}
          />
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
          <Button variant="secondary" type="button" onClick={onClose}>
            {t("tasks.form.cancel")}
          </Button>
          <Button type="submit" loading={saving}>
            {saving
              ? t("tasks.form.saving")
              : isEdit
              ? t("tasks.form.save")
              : t("tasks.form.create")}
          </Button>
        </div>

      </form>
    </Modal>
  );
};
